import i18n from '@providers/i18n';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ToastType } from '@shared/ui/Toast/models/ToastEnum';

export enum NotificationActions {
  SIMPLE_MESSAGE = 'simpleMessage',
  NEW_RECORD = 'newRecord',
  LOST_ITEM_CLAIM_SUBMITTED = 'lostItemClaimSubmitted',
  CLIENT_SHARE_ZIP_READY = 'clientShareZipReady',
  CLIENT_SHARE_ZIP_EMPTY = 'clientShareZipEmpty',
}

const notificationActionsByPages: Partial<Record<NotificationActions, string[]>> = {
  [NotificationActions.NEW_RECORD]: ['/'],
};

interface Action {
  id: string;
  action: NotificationActions;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Record<string, any>;
}

interface Toast {
  id: string;
  title?: string;
  description?: string;
  type: ToastType;
}

interface Subscription {
  id: string;
  event: NotificationActions;
}

interface GlobalAlertState {
  toasts: Toast[];
  actions: Action[];
  subscriptions: Subscription[];
  claimReminders: ClaimReminder[];
  seenClaimReminders: ClaimReminder[];
}

export interface ClaimReminderScope {
  accountId: number;
  hotelId: number;
}

export interface ClaimReminder {
  claimId: string;
  recordId: number;
  accountId: number;
  hotelId: number;
}

interface NotificationContext {
  scope?: ClaimReminderScope;
  canReceiveClaimReminders?: boolean;
}

const initialState: GlobalAlertState = {
  toasts: [],
  actions: [],
  subscriptions: [],
  claimReminders: [],
  seenClaimReminders: [],
};

const isClaimSubmittedPayload = (
  value: unknown,
): value is { hotelId: number; recordId: number; claimId: string } => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const payload = value as Record<string, unknown>;
  const keys = Object.keys(payload).sort();
  return (
    keys.length === 3 &&
    keys[0] === 'claimId' &&
    keys[1] === 'hotelId' &&
    keys[2] === 'recordId' &&
    typeof payload.hotelId === 'number' &&
    Number.isFinite(payload.hotelId) &&
    payload.hotelId >= 0 &&
    Number.isSafeInteger(payload.hotelId) &&
    typeof payload.recordId === 'number' &&
    Number.isFinite(payload.recordId) &&
    payload.recordId > 0 &&
    Number.isSafeInteger(payload.recordId) &&
    typeof payload.claimId === 'string' &&
    payload.claimId.trim().length > 0
  );
};

const notificationSlice = createSlice({
  name: 'notificationSlice',
  initialState,
  reducers: {
    deleteSubscription: (state, action: PayloadAction<string>) => {
      state.subscriptions = state.subscriptions.filter(s => s.id !== action.payload);
    },
    deleteSubscriptionsByEvent: (state, action: PayloadAction<NotificationActions>) => {
      state.subscriptions = state.subscriptions.filter(s => s.event !== action.payload);
    },
    clearClaimReminders: (
      state,
      action: PayloadAction<{ scope?: ClaimReminderScope; claimIds?: string[] } | undefined>,
    ) => {
      const { scope, claimIds } = action.payload ?? {};
      if (!scope && !claimIds) {
        state.seenClaimReminders = [];
        state.toasts = state.toasts.filter(toast => !toast.id.startsWith('lost-item-claim:'));
      }
      state.claimReminders = state.claimReminders.filter(reminder => {
        if (scope && (reminder.accountId !== scope.accountId || reminder.hotelId !== scope.hotelId))
          return true;
        if (claimIds && !claimIds.includes(reminder.claimId)) return true;
        return false;
      });
    },
    addToast: (
      state,
      action: PayloadAction<{
        title?: string;
        message: string;
        type: ToastType;
      }>,
    ) => {
      state.toasts.push({
        id: Date.now().toString(),
        title: action.payload.title,
        description: action.payload.message,
        type: action.payload.type,
      });
    },
    removeToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter(toast => toast.id !== action.payload);
    },
    removeAction: (state, action: PayloadAction<string>) => {
      state.actions = state.actions.filter(a => a.id !== action.payload);
    },
    sendNotification: (
      state,
      action: PayloadAction<{ action: NotificationActions; payload: string } & NotificationContext>,
    ) => {
      let data: Record<string, unknown>;
      try {
        const parsed: unknown = JSON.parse(action.payload.payload);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return;
        data = parsed as Record<string, unknown>;
      } catch {
        return;
      }

      if (action.payload.action === NotificationActions.LOST_ITEM_CLAIM_SUBMITTED) {
        const { scope, canReceiveClaimReminders } = action.payload;
        if (
          !canReceiveClaimReminders ||
          !scope ||
          !Number.isSafeInteger(scope.accountId) ||
          scope.accountId <= 0 ||
          !isClaimSubmittedPayload(data) ||
          data.hotelId !== scope.hotelId
        )
          return;
        if (
          state.seenClaimReminders.some(
            reminder =>
              reminder.claimId === data.claimId &&
              reminder.hotelId === scope.hotelId &&
              reminder.accountId === scope.accountId,
          )
        )
          return;

        const reminder = { ...scope, claimId: data.claimId, recordId: data.recordId };
        state.claimReminders.push(reminder);
        state.seenClaimReminders.push(reminder);
        if (window.location.pathname === '/lost-items') {
          state.toasts.push({
            id: `lost-item-claim:${scope.accountId}:${scope.hotelId}:${data.claimId}`,
            title: i18n.t('notifications.lostItemClaim.title'),
            description: i18n.t('notifications.lostItemClaim.message'),
            type: 'success',
          });
        }
        return;
      }

      const newAction = {
        id: Date.now().toString(),
        action: action.payload.action,
        // Legacy notifications intentionally keep their existing untyped payload contract.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: data as Record<string, any>,
      };
      state.actions.push(newAction);

      // Send out an alert to the user
      switch (action.payload.action) {
        case NotificationActions.SIMPLE_MESSAGE:
          state.toasts.push({
            id: Date.now().toString(),
            title: newAction.data.title,
            description: newAction.data.message,
            type: 'custom',
          });
          break;

        case NotificationActions.NEW_RECORD:
          // 这个只需要在首页展示，如果用户在其他页面不需要提示
          if (
            notificationActionsByPages[NotificationActions.NEW_RECORD]?.includes(
              window.location.pathname,
            )
          ) {
            state.toasts.push({
              id: Date.now().toString(),
              title: i18n.t('notifications.newRecord.title'),
              description: i18n.t('notifications.newRecord.message'),
              type: 'success',
            });
            state.subscriptions.push({
              id: Date.now().toString(),
              event: NotificationActions.NEW_RECORD,
            });
          }
          break;
      }
    },
  },
});

export const {
  addToast,
  removeToast,
  removeAction,
  sendNotification,
  deleteSubscription,
  deleteSubscriptionsByEvent,
  clearClaimReminders,
} = notificationSlice.actions;

export const selectNotificationToasts = (state: { notification: GlobalAlertState }) =>
  state.notification.toasts;

export const selectSubscriptions = (state: { notification: GlobalAlertState }) =>
  state.notification.subscriptions;

export const selectClaimReminders = (state: { notification: GlobalAlertState }) =>
  state.notification.claimReminders;

export default notificationSlice.reducer;
